import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Settings, Briefcase, MapPin, SquareTerminal, Package2, FolderTree, ShoppingCart, TicketPercent, Link2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  getHierarchyDisplayLabelFromInstance,
  getHierarchyTierCodeFromInstance,
  isBranchHierarchyTier,
} from '@/lib/org-hierarchy-from-instance'
import { evaluateRouteAccess, postHasAnyCapabilities } from '@/lib/route-access'
import {
  buildOrgServiceInstanceFormPath,
  getServiceInstanceIdFromSearch,
  withServiceInstanceId,
} from '@/lib/service-instance'
import { getCanonicalServiceVariantLabel } from '@/lib/service-variant-types'
import { orgTreeApi } from '@/services/org-tree'
import { useAuthStore } from '@/stores/auth-store'

type QuickAppLink = { title: string; description: string; href: string; icon: typeof Briefcase }

export default function BranchWorkspacePage() {
  const location = useLocation()
  const currentPost = useAuthStore((state) => state.currentPost)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const instanceQuery = useQuery({
    queryKey: ['branch-workspace-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const variantId = String(
    instanceQuery.data?.service_variant
      ?? instanceQuery.data?.service_data?.variant_unique
      ?? currentPost?.service_variant_unique_id
      ?? '',
  ).trim()
  const variantLabel = String(
    instanceQuery.data?.service_variant_name
      ?? instanceQuery.data?.type
      ?? currentPost?.service_variant_name
      ?? currentPost?.service_variant_type
      ?? '',
  ).trim()

  const isBranchContext = isBranchHierarchyTier(instanceQuery.data)
  const workspaceBlocked = instanceQuery.isSuccess && !isBranchContext

  const canOpenBranchSettings = postHasAnyCapabilities(currentPost, [
    'can_access_legacy_node_settings',
    'can_access_organization_workspace',
    'can_view_organization_tree',
  ])

  const primaryNodeId = useMemo(() => {
    const raw = instanceQuery.data?.primary_node
    if (raw == null || raw === '') {
      return ''
    }
    return String(raw)
  }, [instanceQuery.data?.primary_node])

  const catalogTierLabel = useMemo(() => {
    if (!getHierarchyTierCodeFromInstance(instanceQuery.data)) {
      return null
    }
    return getHierarchyDisplayLabelFromInstance(instanceQuery.data)
  }, [instanceQuery.data])

  const settingsHref = useMemo(() => {
    if (!primaryNodeId || !serviceInstanceId) {
      return null
    }
    const params = new URLSearchParams({
      action: 'edit',
      id: serviceInstanceId,
      tab: 'details',
    })
    return withServiceInstanceId(buildOrgServiceInstanceFormPath(primaryNodeId, params), serviceInstanceId)
  }, [primaryNodeId, serviceInstanceId])

  const metaCredentialsHref = useMemo(() => {
    if (!serviceInstanceId) {
      return null
    }
    return withServiceInstanceId('/organization/settings/credentials/meta', serviceInstanceId)
  }, [serviceInstanceId])

  const instanceTitle = instanceQuery.data?.name || instanceQuery.data?.title || currentPost?.service_instance_name || 'Branch'

  const applicationLinks: QuickAppLink[] = useMemo(() => {
    const raw: QuickAppLink[] = [
      { title: 'Jobs', description: 'Operational jobs and scheduling.', href: '/job', icon: Briefcase },
      { title: 'Locations', description: 'Sites and service coverage.', href: '/locations', icon: MapPin },
      {
        title: 'New Booking',
        description: 'Create a new booking.',
        href: withServiceInstanceId('/services/pos', serviceInstanceId),
        icon: SquareTerminal,
      },
      { title: 'Services', description: 'Service catalog.', href: withServiceInstanceId('/services/products', serviceInstanceId), icon: Package2 },
      {
        title: 'Service categories',
        description: 'Group offerings for menus and shelves.',
        href: withServiceInstanceId('/services/categories', serviceInstanceId),
        icon: FolderTree,
      },
      {
        title: 'Bookings',
        description: 'Booking pipeline.',
        href: withServiceInstanceId('/services/orders', serviceInstanceId),
        icon: ShoppingCart,
      },
      {
        title: 'Offers',
        description: 'Promotions and pricing.',
        href: withServiceInstanceId('/services/offers', serviceInstanceId),
        icon: TicketPercent,
      },
    ]

    return raw.filter((entry) => evaluateRouteAccess(entry.href, currentPost).authorized)
  }, [currentPost, serviceInstanceId])

  if (!serviceInstanceId) {
    return (
      <div className="rounded-[28px] border border-destructive/30 bg-card p-8 text-center">
        <p className="text-sm font-medium text-destructive">Missing service context</p>
        <p className="mt-2 text-sm text-muted-foreground">Select an organization or branch again from the role chooser.</p>
      </div>
    )
  }

  if (instanceQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 w-full rounded-[28px]" />
        <Skeleton className="h-56 w-full rounded-[28px]" />
      </div>
    )
  }

  if (instanceQuery.error || !instanceQuery.data) {
    return (
      <div className="rounded-[28px] border border-destructive/30 bg-card p-8 text-center">
        <p className="text-sm font-medium text-destructive">Could not load branch details</p>
      </div>
    )
  }

  if (workspaceBlocked) {
    return (
      <div className="rounded-[28px] border border-border/80 bg-card p-8 text-center">
        <p className="text-base font-medium text-foreground">This workspace is for branch roles</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Open <Link className="text-primary underline" to={withServiceInstanceId('/org/organization', serviceInstanceId)}>My Organisation</Link> instead.
        </p>
      </div>
    )
  }

  const variantBadge = getCanonicalServiceVariantLabel(variantId, variantLabel) || variantLabel || 'Branch'

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardHeader className="flex flex-col gap-4 border-b border-border/70 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <CardDescription>My Branch</CardDescription>
            <CardTitle className="text-3xl tracking-tight">{instanceTitle}</CardTitle>
            <p className="text-sm text-muted-foreground">
              Application operations and daily tools. Organisation-wide administration stays under My Organisation.
            </p>
            <p className="text-xs text-muted-foreground">
              {catalogTierLabel ? (
                <>
                  Catalog tier: <span className="font-medium text-foreground">{catalogTierLabel}</span>
                  {' · '}
                </>
              ) : null}
              Application variant: <span className="font-medium text-foreground">{variantBadge}</span>
              {instanceQuery.data.instance_id ? (
                <>
                  {' '}
                  · Service ID: <span className="font-mono text-foreground">{instanceQuery.data.instance_id}</span>
                </>
              ) : null}
            </p>
          </div>
          {canOpenBranchSettings ? (
            <div className="flex shrink-0 flex-wrap gap-2">
              {metaCredentialsHref ? (
                <Button variant="outline" className="gap-2" asChild>
                  <Link to={metaCredentialsHref}>
                    <Link2 className="h-4 w-4" />
                    Meta credentials
                  </Link>
                </Button>
              ) : null}
              {settingsHref ? (
                <Button variant="outline" className="gap-2" asChild>
                  <Link to={settingsHref}>
                    <Settings className="h-4 w-4" />
                    Branch settings
                  </Link>
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardHeader>
      </Card>

      {applicationLinks.length > 0 ? (
        <Card className="rounded-[28px] border-border/80">
          <CardHeader>
            <CardTitle className="text-lg">Applications</CardTitle>
            <CardDescription>
              Applications and tools available here depend on what is enabled for this branch and your access.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {applicationLinks.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.href}
                  to={withServiceInstanceId(item.href, serviceInstanceId)}
                  className="group rounded-2xl border border-border/70 bg-card/80 p-5 transition-colors hover:border-primary/40 hover:bg-primary/5"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 space-y-1">
                      <p className="font-semibold text-foreground group-hover:text-primary">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </Link>
              )
            })}
          </CardContent>
        </Card>
      ) : null}
      <p className="text-center text-sm text-muted-foreground">
        Vendors, suppliers, and users are available from the sidebar under your organization tools.
      </p>
    </div>
  )
}
